import { useState } from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { Camera, Upload, Loader2 } from "lucide-react"
import { UploadImageInput, useUploadImage } from "@/graphql/hooks/useUploadImage"

interface AvatarUploadProps {
  src?: string
  name: string
  type: UploadImageInput["type"]
  personId?: number
  field?: string
  tournamentTypeId?: number
  gameId?: number
  className?: string
  cb?: () => unknown
}

export const AvatarUpload = ({
  src,
  name,
  personId,
  gameId,
  type,
  tournamentTypeId,
  className,
  field,
  cb,
}: AvatarUploadProps) => {
  const [preview, setPreview] = useState<string | null>(null)
  const { uploadImageUser, loading } = useUploadImage()

  const convertToBase64 = (file: File): Promise<string> =>
    new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const result = reader.result as string
        resolve(result.split(",")[1])
      }
      reader.onerror = reject
      reader.readAsDataURL(file)
    })

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    const validTypes = ["image/jpeg", "image/jpg", "image/png"]
    if (!validTypes.includes(file.type)) {
      alert("Solo se permiten archivos JPG y PNG")
      e.target.value = ""
      return
    }

    const maxSize = 1 * 1024 * 1024
    if (file.size > maxSize) {
      alert("El archivo no debe exceder 1MB")
      e.target.value = ""
      return
    }

    try {
      const previewUrl = URL.createObjectURL(file)
      setPreview(previewUrl)

      const base64 = await convertToBase64(file)
      await uploadImageUser({
        personId,
        image: base64,
        name: file.name,
        field,
        type,
        gameId,
        tournamentTypeId,
      })

      cb?.()
    } catch (err) {
      console.error("Error uploading image:", err)
      setPreview(null)
    } finally {
      e.target.value = ""
    }
  }

  const displaySrc = preview || src

  return (
    <div className={cn("flex flex-col items-center gap-4", className)}>
      {/* Área de dropzone */}
      <label
        className={cn(
          "relative flex flex-col items-center justify-center border-2 border-dashed rounded-xl cursor-pointer transition hover:bg-muted/40",
          "w-48 h-48 p-1 text-center",
          className,
        )}
      >
        {displaySrc ? (
          <img
            src={displaySrc}
            alt={name}
            className="object-cover w-full h-full rounded-lg shadow-sm"
          />
        ) : (
          <div className="flex flex-col items-center gap-2 text-muted-foreground">
            <Camera className="w-8 h-8" />
            <span className="text-sm font-medium">Haz clic para subir imagen</span>
            <span className="text-xs text-muted-foreground">(JPG o PNG, máx 1MB)</span>
          </div>
        )}

        {loading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-xl">
            <Loader2 className="w-6 h-6 text-white animate-spin" />
          </div>
        )}

        {/* El input real */}
        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleFileChange}
          className="sr-only"
        />
      </label>

      {/* Botón secundario -> también label */}
      <label>
        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleFileChange}
          className="sr-only"
        />
        <Button type="button" disabled={loading} asChild>
          <span className="flex items-center gap-2">
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Upload className="w-4 h-4" />
            )}
            {displaySrc ? "Cambiar imagen" : "Subir imagen"}
          </span>
        </Button>
      </label>
    </div>
  )
}
